import { messageVersion } from './Constants';
import { MessageType, } from './Interfaces';
/**
 * Enum for checking if message is valid
 * VALID: the message is valid, and should be picked up by Satellite shell side
 * INVALID: the message is invalid due to some issues, Satellite should reject the handshake Promise
 * INCORRECT_TOKEN: the message does not contain valid token
 * IGNORE: the message is not from allowed origin, we should ignore since it is not from the remote components
 * enum {string}
 * */
export var validMessageType;
(function (validMessageType) {
    validMessageType["VALID"] = "Valid";
    validMessageType["INVALID"] = "Invalid";
    validMessageType["INCORRECT_TOKEN"] = "Incorrect Token";
    validMessageType["IGNORE"] = "Ignore";
})(validMessageType || (validMessageType = {}));
/**
 * Used as an identifier for the messages to ensure responses are sent to the correct requests.
 * @type {Number}
 */
let _sequenceId = 0;
/**
 * Increments and returns a message ID
 * @return {Number} A unique ID for a message
 */
export const generateNewMessageId = () => ++_sequenceId;
/**
 * Takes a URL and returns the origin
 * @param  {String} url The full URL being requested
 * @return {String}     The URLs origin
 */
export const resolveOrigin = (urlString) => {
    const url = new URL(urlString);
    const protocol = url.protocol.length > 4 ? url.protocol : window.location.protocol; // eslint-disable-line no-restricted-syntax
    const host = url.host.length
        ? url.port === '80' || url.port === '443'
            ? url.hostname
            : url.host
        : window.location.host; // eslint-disable-line no-restricted-syntax
    return url.origin || `${protocol}//${host}`;
};
/**
 * Ensures that a message is valid.
 * @param  {Object} messageEvent
 * @param  {String|Boolean} allowedOrigin The whitelisted origin or false to skip origin check
 * @param  {String|Boolean} allowedSatelliteToken The satellite token or false to skip token check
 * @return {String}
 */
export const validateMessage = (messageEvent, allowedOrigin, allowedSatelliteToken) => {
    if (messageEvent === undefined) {
        return validMessageType.INVALID;
    }
    if (!(messageEvent.target instanceof MessagePort) &&
        typeof allowedOrigin === 'string' &&
        messageEvent.origin !== allowedOrigin) {
        // The message is not from the origin we are checking
        return validMessageType.IGNORE;
    }
    if (!messageEvent.data) {
        return validMessageType.INVALID;
    }
    if (typeof messageEvent.data === 'object' &&
        !('type' in messageEvent.data)) {
        return validMessageType.INVALID;
    }
    if (messageEvent.data.messageVersion !== messageVersion) {
        return validMessageType.INVALID;
    }
    if (!Object.values(MessageType).includes(messageEvent.data.type)) {
        return validMessageType.INVALID;
    }
    if (typeof allowedSatelliteToken === 'string' &&
        messageEvent.data.satelliteToken !== allowedSatelliteToken) {
        return validMessageType.INCORRECT_TOKEN;
    }
    return validMessageType.VALID;
};
/**
 * Utility for constructing serializable satellite props that can be sent in a handshake or other message.
 * Handshake is performed through messagePassing & hence the constructed props must be serializable.
 * In particular, functions and event buses are serialized to their key names (and config values)
 * @param props
 */
export function constructSerializableSatelliteProps(props) {
    const serializableProps = {};
    if (props == undefined) {
        return serializableProps;
    }
    let servicesArray = [];
    // 'services' is a reserved keyword by the library for representing an array of functions.
    // Since, functions are non serializable we are only going to pass the function names.
    if (props.services) {
        servicesArray = Object.keys(props.services);
    }
    let eventsArray = [];
    // 'events' is a reserved keyword by the library for representing an array of SatelliteEventBus.
    if (props.events) {
        const events = props.events; // copy so Typescript knows it's non-null
        eventsArray = Object.keys(events).map((eventName) => {
            return {
                eventName,
                config: events[eventName].getSerializableConfig(),
            };
        });
    }
    return Object.assign(Object.assign({}, props), { services: servicesArray, events: eventsArray });
}
//# sourceMappingURL=SatelliteUtil.js.map